import Vector from './vector'

// TODO: rewrite using lodash

// Type definitions
export type LandmarkIndex = number
export type JointDefinition = [number, number, number]
export type PoseData = number[][]
export type JointAngles = Record<string, { angle: number; conf: number }>
export type JointWeights = Record<string, number>

/**
 * Pose landmark indices for body pose detection using BlazePose.
 * These indices correspond to the 33 keypoints detected by MediaPipe BlazePose.
 * @constant {Object}
 */
export const Landmark = {
  // Face landmarks
  NOSE: 0,
  LEFT_EYE_INNER: 1,
  LEFT_EYE: 2,
  LEFT_EYE_OUTER: 3,
  RIGHT_EYE_INNER: 4,
  RIGHT_EYE: 5,
  RIGHT_EYE_OUTER: 6,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  MOUTH_LEFT: 9,
  MOUTH_RIGHT: 10,

  // Upper body landmarks
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_PINKY: 17,
  RIGHT_PINKY: 18,
  LEFT_INDEX: 19,
  RIGHT_INDEX: 20,
  LEFT_THUMB: 21,
  RIGHT_THUMB: 22,

  // Lower body landmarks
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
}

export const LandmarkName = [
  'nose',
  'left eye inner',
  'left eye',
  'left eye outer',
  'right eye_inner',
  'right eye',
  'right eye outer',
  'left ear',
  'right ear',
  'mouth left',
  'mouth right',
  'left shoulder',
  'right shoulder',
  'left elbow',
  'right elbow',
  'left wrist',
  'right wrist',
  'left pinky',
  'right pinky',
  'left index',
  'right index',
  'left thumb',
  'right thumb',
  'left hip',
  'right hip',
  'left knee',
  'right knee',
  'left ankle',
  'right ankle',
  'left heel',
  'right heel',
  'left foot',
  'right foot',
]

// does not work for face landmarks
export function mirrorLandmark (landmark: LandmarkIndex): LandmarkIndex {
  if (landmark > 6) {
    return landmark + (2 * (landmark % 2) - 1)
  } else if (landmark > 3) {
    return landmark - 3
  } else if (landmark > 0) {
    return landmark + 3
  }

  return landmark
}

export function mirrorJoint (joint: JointDefinition): JointDefinition {
  return joint.map(landmark => mirrorLandmark(landmark)) as JointDefinition
}

export function mirrorKey (key: string): string {
  if (key.startsWith('LEFT_'))
    return key.replace('LEFT_', 'RIGHT_')
  else
    return key.replace('RIGHT_', 'LEFT_')
}

/**
 * Joint definitions for calculating joint angles.
 * Each joint is defined by three landmarks that form the joint angle.
 * Note: Not all joints are present in this mapping because hands and especially feet aren't that accurate,
 * but it could make sense to add them in the future, as well as neck joint.
 * @constant {Object}
 */
export const Joints: Record<string, JointDefinition> = {
  LEFT_SHOULDER_HIP_KNEE: [
    Landmark.LEFT_SHOULDER,
    Landmark.LEFT_HIP,
    Landmark.LEFT_KNEE,
  ],

  RIGHT_SHOULDER_HIP_KNEE: [
    Landmark.RIGHT_SHOULDER,
    Landmark.RIGHT_HIP,
    Landmark.RIGHT_KNEE,
  ],


  LEFT_SHOULDER_ELBOW_WRIST: [
    Landmark.LEFT_SHOULDER,
    Landmark.LEFT_ELBOW,
    Landmark.LEFT_WRIST,
  ],

  RIGHT_SHOULDER_ELBOW_WRIST: [
    Landmark.RIGHT_SHOULDER,
    Landmark.RIGHT_ELBOW,
    Landmark.RIGHT_WRIST,
  ],

  LEFT_ELBOW_SHOULDER_SHOULDER: [
    Landmark.LEFT_ELBOW,
    Landmark.LEFT_SHOULDER,
    Landmark.RIGHT_SHOULDER,
  ],

  RIGHT_ELBOW_SHOULDER_SHOULDER: [
    Landmark.RIGHT_ELBOW,
    Landmark.RIGHT_SHOULDER,
    Landmark.LEFT_SHOULDER,
  ],

  LEFT_ELBOW_SHOULDER_HIP: [
    Landmark.LEFT_ELBOW,
    Landmark.LEFT_SHOULDER,
    Landmark.LEFT_HIP,
  ],

  RIGHT_ELBOW_SHOULDER_HIP: [
    Landmark.RIGHT_ELBOW,
    Landmark.RIGHT_SHOULDER,
    Landmark.RIGHT_HIP,
  ],

  LEFT_HIP_SHOULDER_WRIST: [
    Landmark.LEFT_HIP,
    Landmark.LEFT_SHOULDER,
    Landmark.LEFT_WRIST,
  ],

  RIGHT_HIP_SHOULDER_WRIST: [
    Landmark.RIGHT_HIP,
    Landmark.RIGHT_SHOULDER,
    Landmark.RIGHT_WRIST,
  ],

  LEFT_HIP_KNEE_ANKLE: [
    Landmark.LEFT_HIP,
    Landmark.LEFT_KNEE,
    Landmark.LEFT_ANKLE,
  ],

  RIGHT_HIP_KNEE_ANKLE: [
    Landmark.RIGHT_HIP,
    Landmark.RIGHT_KNEE,
    Landmark.RIGHT_ANKLE,
  ],

  LEFT_KNEE_HIP_HIP: [
    Landmark.LEFT_KNEE,
    Landmark.LEFT_HIP,
    Landmark.RIGHT_HIP,
  ],

  RIGHT_KNEE_HIP_HIP: [
    Landmark.RIGHT_KNEE,
    Landmark.RIGHT_HIP,
    Landmark.LEFT_HIP,
  ],
}

/**
 * Skeleton connections defining which keypoints are connected by bones.
 * Each sub-array contains two indices representing connected keypoints.
 * @constant {Array<Array<number>>}
 */
export const Connections = [[0, 1], [0, 4], [1, 2], [2, 3], [3, 7], [4, 5], [5, 6], [6, 8], [9, 10], [11, 12], [11, 13], [11, 23], [12, 14], [14, 16], [12, 24], [13, 15], [15, 17], [16, 18], [16, 20], [15, 17], [15, 19], [15, 21], [16, 22], [17, 19], [18, 20], [23, 25], [23, 24], [24, 26], [25, 27], [26, 28], [27, 29], [28, 30], [27, 31], [28, 32], [29, 31], [30, 32]]

/** @constant {number} Total number of keypoints in BlazePose output */
export const NUM_LANDMARKS = 33
/** @constant {number} Total number of joints defined for angle calculations */
export const NUM_JOINTS = Object.keys(Joints).length

/**
 * Converts a joint key to a shortened name for display purposes.
 * @param {string} key - The joint key (e.g., "LEFT_SHOULDER_HIP_KNEE")
 * @returns {string} Shortened joint name (e.g., "L_sho_hip_kne")
 */
export function jointKeyToShortName (key: string): string {
  const parts = key.split('_')
  const side = parts.shift()![0]
  const shortName = parts.map((part: string) => part.slice(0, 3).toLowerCase()).join('_')

  return `${side}_${shortName}`
}

/**
 * Constrains a value between a minimum and maximum value.
 * @param {number} value - Value to constrain
 * @param {number} minVal - Minimum value
 * @param {number} maxVal - Maximum value
 * @returns {number} Constrained value
 */
export function constrain (value: number, minVal: number, maxVal: number): number {
  return Math.max(minVal, Math.min(value, maxVal))
}

/**
 * Pose class representing BlazePose output data.
 * Handles 3D keypoint data, confidence scores, and 2D positions.
 * It can align the pose and calculate joint angles.
 */
export class Pose {
  public data: PoseData
  public jointAngles!: JointAngles
  /**
   * Creates a new Pose instance from BlazePose output data.
   * @param {Array} data - BlazePose output concatenated (world_landmarks[xyz] || confidence || local_landmarks[xyz])
   * @throws {Error} If data length doesn't match expected number of joints
   */
  constructor (data: PoseData) {
    this.data = data
    this.calcAllJointAngles()
  }

  // fromMediaPipeResults (results: MediaPipePoseResults): Pose {
  //   return new Pose(results.poseWorldLandmarks.map((keypoint: any, index: number) => [keypoint.x, keypoint.y, keypoint.z, keypoint.visibility, results.poseLandmarks[index].x, results.poseLandmarks[index].y, results.poseLandmarks[index].z]))
  // }

  /**
   * Creates a new Pose instance from the current Pose instance.
   * @returns {Pose} New Pose instance
   */
  clone (): Pose {
    return new Pose(this.data.map((keypoint) => keypoint.slice()))
  }

  /**
   * Gets the 3D position of a keypoint as a Vector.
   * @param {number} idx - Keypoint index
   * @returns {Vector} 3D position vector
   */
  keypoint (idx: LandmarkIndex): Vector {
    return new Vector(...this.data[idx].slice(0, 3))
  }

  /**
   * Gets the confidence score for a keypoint.
   * @param {number} idx - Keypoint index
   * @returns {number} Confidence score (0-1)
   */
  conf (idx: LandmarkIndex): number {
    return this.data[idx][3]
  }

  /**
   * Gets the 2D position of a keypoint as a Vector.
   * Note: returns zero vector if 2D data not present
   * @param {number} idx - Keypoint index
   * @returns {Vector} 2D position vector
   */
  pos2d (idx: LandmarkIndex): Vector {
    // NOTE: if data.length <= 4 the slice will return empty array, unpacked to undefined, resulting in zero vector
    // perhaps there's a better way to handle this but it works for now
    return new Vector(...this.data[idx].slice(4, 7))
  }

  /**
   * Calculates the center point between left and right shoulders.
   * @returns {Vector} Center point between left and right shoulders
   */
  centerShoulders (): Vector {
    return this.keypoint(Landmark.LEFT_SHOULDER).add(this.keypoint(Landmark.RIGHT_SHOULDER)).div(2)
  }

  centerBody (): Vector {
    return this.centerShoulders().div(2)
  }

  confJoint (joint: JointDefinition, method: (...args: number[]) => number = Math.min): number {
    return method(...joint.map((i: number) => this.conf(i)))
  }

  /**
   * Calculates the angle between three keypoints forming a joint angle.
   * @param {Array<number>} joint - Array of three keypoint indices [iA, iB, iC] where iB is the joint center
   * @returns {number} Joint angle in degrees
   * @throws {Error} If joint array doesn't contain exactly 3 indices
   */
  calcJointAngle (joint: JointDefinition): number {
    if (joint.length != 3)
      throw new Error('Invalid joint')

    const [iA, iB, iC] = joint
    const pA = this.keypoint(iA)
    const pB = this.keypoint(iB)
    const pC = this.keypoint(iC)

    const edgeAB = pA.sub(pB)
    const edgeCB = pC.sub(pB)

    const dotProduct = edgeAB.dot(edgeCB)
    const mag1 = edgeAB.length()
    const mag2 = edgeCB.length()

    const radians = Math.acos(dotProduct / (mag1 * mag2))

    return radians * 180 / Math.PI
  }

  /**
   * Calculates the normal vector of a triangle formed by three joints.
   * @param {Array<number>} joint - Array of three joint indices [iA, iB, iC]
   * @returns {Vector} Normal vector of the triangle
   */
  triNormal (joint: JointDefinition): Vector {
    const [iA, iB, iC] = joint
    const edgeBA = this.keypoint(iB).sub(this.keypoint(iA))
    const edgeCB = this.keypoint(iC).sub(this.keypoint(iB))

    return edgeBA.cross(edgeCB).norm_inplace()
  }

  /**
   * Calculates the forward normal and rotation information using hip positions.
   * Used for pose alignment and orientation detection.
   * @returns {Object} Object containing forwardNormal, rotationAxis, and rotationAngle
   */
  calcRotation (): { forwardNormal: Vector; rotationAxis: Vector; rotationAngle: number; forwardBody: Vector } {
    const forwardGlobal = new Vector(0, 0, -1)

    const leftHip = this.keypoint(Landmark.LEFT_HIP)
    const rightHip = this.keypoint(Landmark.RIGHT_HIP)

    const forwardBody = this.triNormal([Landmark.LEFT_SHOULDER, Landmark.LEFT_HIP, Landmark.RIGHT_HIP])

    // TODO: fix forward normal as it is incorrect when pose is upside-down
    // see: Barbell Feet Elevated Glute Bridge_male-barbell-feet-elevated-glute-bridge-front.json
    // the forwardBody is correct, need to take that
    const forwardNormal = new Vector(
      -(leftHip.z - rightHip.z),
      0,
      leftHip.x - rightHip.x,
    ).norm_inplace()

    // Compute rotation axis (cross product)
    const rotationAxis = forwardNormal.cross(forwardGlobal)

    // Compute angle (arccos of dot product)
    let dot = forwardNormal.dot(forwardGlobal)
    dot = Math.max(Math.min(dot, 1), -1) // avoid NaN
    const rotationAngle = Math.acos(dot)

    return { forwardNormal, rotationAxis, rotationAngle, forwardBody }
  }

  /**
   * Aligns the pose to a standard orientation by rotating around the calculated rotation axis.
   * @param {number} defaultAngle - Default rotation angle to use if calculation fails
   * @returns {Pose} New aligned Pose instance
   */
  alignPose (): Pose {
    const { rotationAxis, rotationAngle } = this.calcRotation()

    const newPose = this.data.slice(0, NUM_LANDMARKS)
    for (let i = 0; i < this.data.length; i++) {
      const keypoint = this.keypoint(i).rotateAround_inplace(
        rotationAxis,
        rotationAngle,
      )

      newPose[i][0] = keypoint.x
      newPose[i][1] = keypoint.y
      newPose[i][2] = keypoint.z
    }

    return new Pose(newPose)
  }

  /**
   * Calculates and caches all joint angles for performance.
   * Stores results in this.angles object.
   */
  calcAllJointAngles (): void {
    this.jointAngles = {}
    for (const [key, joint] of Object.entries(Joints)) {
      this.jointAngles[key] = {
        angle: this.calcJointAngle(joint),
        conf: this.confJoint(joint),
      }
    }
  }

  /**
   * Calculates the distance between two poses by measuring the square of the difference
   * in joint angles. Lower values indicate more similar poses.
   * @param {Pose} other - Other pose to compare against
   * @param {Object} weights - Optional weights for different joints (not currently used)
   * @returns {number} Distance metric between poses
   */
  calcPoseDifference (other: Pose, weights: JointWeights, mirror = false, threshold = 0.9): number {
    let dist = 0
    for (const key in Joints) {
      if (!weights[key]) {
        continue
      }

      const a1 = this.jointAngles[key]
      const a2 = other.jointAngles[mirror ? mirrorKey(key) : key]

      let diff = Math.abs(a1.angle - a2.angle) * weights[key]
      if (a1.conf < threshold || a2.conf < threshold) {
        diff /= Math.min(a1.conf, a2.conf)
      }

      dist += diff * diff
    }

    return Math.sqrt(dist)
  }

  calcPoseScore (other: Pose, weights: JointWeights, mirror = false): number {
    const diff = this.calcPoseDifference(other, weights, mirror)
    const score = (300 - constrain(diff, 0, 300)) / 300.0

    return score
  }
}

/**
 * Motion class for managing sequences of poses over time.
 * Provides pose caching, smoothing, and temporal analysis capabilities.
 */
export class Motion {
  public cache_size: number
  public motion: Pose[]

  /**
   * Creates a new Motion instance for managing pose sequences.
   * @param {Object} options - Configuration options
   * @param {Array} options.motion - Array of pose data
   * @param {number} options.cache_size - Maximum number of poses to cache (defaults to motion length)
   */
  constructor ({ motion = [], cache_size = null }: { motion?: Pose[]; cache_size?: number | null } = {}) {
    this.cache_size = cache_size || motion.length || 10
    this.motion = motion.slice(-this.cache_size)
  }

  /**
   * Adds a new pose to the motion cache.
   * @param {Array} pose - Pose data to add
   */
  update (pose: Pose | PoseData): void {
    if (!(pose instanceof Pose)) {
      pose = new Pose(pose)
    }

    this.motion.push(pose)
    if (this.motion.length > this.cache_size) {
      this.motion = this.motion.slice(-this.cache_size)
    }
  }

  /**
   * Gets a pose at a specific index from the motion cache.
   * @param {number} idx - Index of the pose to retrieve (0 = most recent)
   * @returns {Pose|null} Pose at the specified index or null if not found
   */
  getPose (idx = -1): Pose | null {
    if (idx < 0) {
      idx = this.motion.length + idx
    }

    return this.motion[idx]
  }

  /**
   * Gets a range of poses from the motion cache.
   * @param {number} idx - Starting index
   * @param {number} count - Number of poses to retrieve
   * @returns {Array<Pose>} Array of poses
   */
  getPoses (idx: number, count: number): Pose[] {
    if (idx < 0) {
      idx = this.motion.length + idx
    }

    return this.motion.slice(Math.max(idx - count + 1, 0), idx + 1)
  }

  /**
   * Calculates a smoothed pose by averaging multiple poses over time.
   * Uses a naive averaging method (sum / count) for each keypoint.
   * @param {number} idx - Index of the target pose
   * @param {number} smoothing - Number of previous poses to include in smoothing
   * @returns {Pose|null} Smoothed pose or null if no valid poses found
   */
  calcSmoothPose (idx = -1, smoothing = 3): Pose | null {
    const prevPoses = this.getPoses(idx, smoothing).filter((pose): pose is Pose => pose !== null && pose.data.length > 0)
    if (prevPoses.length === 0) {
      return null
    }

    // naive method: average (sum / count)
    const pose = []
    for (let j = 0; j < NUM_LANDMARKS; j++) {
      const sum = new Vector(0, 0, 0)
      let conf = 0
      const sum2d = new Vector(0, 0, 0)

      for (let i = 0; i < prevPoses.length; i++) {
        sum.add_inplace(prevPoses[i].keypoint(j))
        conf += prevPoses[i].conf(j)
        sum2d.add_inplace(prevPoses[i].pos2d(j))
      }

      let avg: number[] = sum.div_inplace(prevPoses.length).toArray()
      avg.push(conf / prevPoses.length)
      avg = avg.concat(sum2d.div_inplace(prevPoses.length).toArray())

      pose.push(avg)
    }

    return new Pose(pose)
  }

  /**
   * Calculates a smoothed pose by averaging multiple poses over time.
   * Uses a naive averaging method (sum / count) for each keypoint.
   * @param {number} idx - Index of the target pose
   * @param {number} smoothing - Number of previous poses to include in smoothing
   * @returns {Pose|null} Smoothed pose or null if no valid poses found
   */
  calcSmoothPoseWeighted (idx = -1, smoothing = 3): Pose | null {
    const prevPoses = this.getPoses(idx, smoothing).filter((pose): pose is Pose => pose !== null && pose.data.length > 0)
    if (prevPoses.length === 0) {
      return null
    }
    // weighted average, with more recent poses having higher weight
    const pose = []
    for (let j = 0; j < NUM_LANDMARKS; j++) {
      const sum = new Vector(0, 0, 0)
      let conf = 0
      const sum2d = new Vector(0, 0, 0)
      let totalWeight = 0

      for (let i = 0; i < prevPoses.length; i++) {
        // Linear weight decay from 1 to ~0
        const weight = (prevPoses.length - i + 1) / prevPoses.length
        totalWeight += weight

        sum.add_inplace(prevPoses[i].keypoint(j).mul(weight))
        conf += prevPoses[i].conf(j) * weight
        sum2d.add_inplace(prevPoses[i].pos2d(j).mul(weight))
      }

      let avg: number[] = sum.div_inplace(totalWeight).toArray()
      avg.push(conf / totalWeight)
      avg = avg.concat(sum2d.div_inplace(totalWeight).toArray())

      pose.push(avg)
    }

    return new Pose(pose)
  }
}

export function trimPrecision (data: number[], decimals = 3) {
  return data.map((val) => parseFloat(val.toFixed(decimals)))
}

export function blazeMapToPose (data: any) {
  // za is what mediapipe holistic gives, idk, seems undocumented...
  const worldLandmarks = data.poseWorldLandmarks || data.za
  if (!worldLandmarks?.length)
    return null

  return new Pose(worldLandmarks.map((keypoint: any, i: number) => trimPrecision([
    keypoint.x,
    keypoint.y,
    keypoint.z,
    keypoint.visibility || 1,
    data.poseLandmarks[i].x,
    data.poseLandmarks[i].y,
    data.poseLandmarks[i].visibility || 1,
  ])))
}

/*
TODO:

- better motion smoothing algos
- come up with pose distance function (compare two poses)
- how to deal with low confidence joints / edges / poses
*/
